import {useCallback, useEffect} from "react";
import {useSignal} from "@preact/signals-react";
import {postSubmit} from "@ui-seeker/utils";

import {BACKEND_APIS} from "@constants";


export default function useProductsMatcherBrandSkus(brandID, siteID, excluded) {
	const isLoading = useSignal(false)
	const searchText = useSignal('')
	const brandsOptions = useSignal([])

	// Holds the AbortController of the latest in-flight request. Promise handlers compare
	// against it so only the most recent request may mutate state — a superseded/aborted
	// request (e.g. when the user navigates to the next product) is ignored, otherwise a
	// late-resolving older request would overwrite the new product's candidates.
	const controller = useSignal(null)

	useEffect(() => {
		return () => {
			if (controller.value) {
				controller.value.abort()
				controller.value = null
			}
		}
	}, [])

	const loadBrandSkus = useCallback(() => {
		if (!brandID || !siteID) return

		if (controller.value) controller.value.abort()
		const myController = new AbortController()
		controller.value = myController

		isLoading.value = true
		postSubmit(`${BACKEND_APIS.MATCHERS}/products/candidates/${brandID}/${siteID}`, {
			find: searchText.value
		}, { signal: myController.signal }).then(res => {
			if (controller.value !== myController) return  // superseded by a newer request
			controller.value = null
			isLoading.value = false
			brandsOptions.value = res.options
		}).catch(() => {
			if (controller.value !== myController) return  // aborted or superseded
			controller.value = null
			isLoading.value = false
		})
	}, [brandID, siteID, searchText.value])

	// Clear candidates the instant the product (brand/site) changes, so the previous product's
	// candidates never linger on screen while the new ones load. Not tied to searchText to avoid
	// flashing the list empty on every keystroke.
	useEffect(() => {
		brandsOptions.value = []
	}, [brandID, siteID])

	useEffect(() => {
		loadBrandSkus()
	}, [brandID, siteID, searchText.value]);

	const handleChangeSearchText = useCallback(newValue => {
		searchText.value = newValue
	}, [])

	return {
		brandOptions: brandsOptions.value.filter(op => searchText.value ? true : !excluded.includes(op.productID)),
		allBrandOptions: brandsOptions.value,
		isLoading: isLoading.value,
		searchText: searchText.value,

		handleChangeSearchText
	}
}
